import dayjs from 'dayjs'
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { ADDRESS_LINE_MAX_LENGTH, MIN_LENGTH } from 'pages/Contacts/constants'
import { BeneficiaryEntityType } from 'types/beneficiaries'
import { createBeneficiaryAddressSchema } from 'utils/yupSchemas/addressSchemas'
import {
  createBeneficiaryNameSchema,
  createGenericNameSchema,
} from 'utils/yupSchemas/createGenericNameSchema'
import { createPaymentReferenceSchema } from 'utils/yupSchemas/createReferenceSchema'
import { getBankAddressFields } from './bankAddressFields'

const SINGLE_DATE_PATTERN = /^(\d{1,2}),(\d{1,2}),(\d{4})$/
const SINGLE_DATE_EMPTY = ',,'

export const getCommonSchemaFields = (t: TFunction<'translation', undefined>) => {
  const beneficiaryEntityType = yup.string().trim().required()
  const beneficiaryType = yup.string().trim().required()

  // Individual Beneficiary
  const firstName = createGenericNameSchema(t('firstName'), t, 2)
  const lastName = createGenericNameSchema(t('lastName'), t, 2)
  const dateOfBirth = yup
    .string()
    .trim()
    .label(t('dateOfBirth'))
    .test({
      test(value, ctx) {
        if (ctx?.parent?.beneficiaryEntityType === BeneficiaryEntityType.INDIVIDUAL) {
          if (value && value !== SINGLE_DATE_EMPTY) {
            if (!SINGLE_DATE_PATTERN.test(value)) {
              return ctx.createError({
                message: t('invalidDateError'),
              })
            }

            const [day, month, year] = value.split(',')
            const dateOfBirthFormatted = `${year}-${month}-${day}`
            const date = dayjs(dateOfBirthFormatted)

            const maxDate = dayjs(new Date())
            if (date.isAfter(maxDate)) {
              return ctx.createError({
                message: t('dateOfBirthFutureDateError'),
              })
            }

            return true
          }

          return true
        }

        return ctx.schema.notRequired()
      },
    })

  // Business beneficiary
  const businessName = createGenericNameSchema(t('businessName'), t)

  // All beneficiaries
  const countryCode = yup.string().label(t('country')).required()
  const currencyLabel = t('currency')
  const currencyCode = yup.string().label(currencyLabel).required()
  const alias = createBeneficiaryNameSchema(t('beneficiaryName'), t)

  const addressLine1Label = t('addressLine1')
  const addressLine1 = createBeneficiaryAddressSchema({
    label: addressLine1Label,
    t,
    minLength: MIN_LENGTH,
    maxLength: ADDRESS_LINE_MAX_LENGTH,
  })

  const addessLine2Label = t('formFields.addressLine2')
  const addressLine2 = createBeneficiaryAddressSchema({
    label: addessLine2Label,
    t,
    minLength: MIN_LENGTH,
    maxLength: ADDRESS_LINE_MAX_LENGTH,
  })

  const cityLabel = t('city')
  const city = createBeneficiaryAddressSchema({
    label: cityLabel,
    t,
    minLength: MIN_LENGTH,
    maxLength: ADDRESS_LINE_MAX_LENGTH,
  })

  const postCodeLabel = t('postCode')
  const postCode = createBeneficiaryAddressSchema({
    label: postCodeLabel,
    t,
    minLength: 3,
    maxLength: ADDRESS_LINE_MAX_LENGTH,
  })

  const regionLabel = t('region')
  const region = createBeneficiaryAddressSchema({
    label: regionLabel,
    t,
    minLength: MIN_LENGTH,
    maxLength: ADDRESS_LINE_MAX_LENGTH,
  })

  const beneficiaryPaymentReference = createPaymentReferenceSchema(
    t('beneficiaryPaymentReference'),
    t
  )

  const {
    bankName,
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankPostCode,
    bankRegion,
    bankCountry,
  } = getBankAddressFields(t)

  return {
    alias,
    beneficiaryEntityType,
    beneficiaryType,
    firstName,
    lastName,
    dateOfBirth,
    businessName,
    countryCode,
    currencyCode,
    addressLine1,
    addressLine2,
    city,
    postCode,
    region,
    beneficiaryPaymentReference,
    // Bank details
    bankName,
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankPostCode,
    bankRegion,
    bankCountry,
  }
}
